// Angular
import { Injectable } from '@angular/core';

// RxJS
import { BehaviorSubject, tap } from 'rxjs';

// Data Contracts
import {
  ClerkReviewQueryRequest, QueryColumnOfString, QueryOperation, ReviewFilingSummary, SortOrder
} from '@data-contracts';

// Local
import { ALL_COURTS_VALUE } from '@feature/crl/clerk-review-filter/court-filter-control/court-filter-control.component';
import { ALL_JUDGES_VALUE } from '@feature/crl/clerk-review-filter/judge-filter-control/judge-filter-control.component';
import { IOtherFilterField } from '@feature/crl/clerk-review-filter/other-filter-fields.model';
import { IOtherFilterOperation } from '@feature/crl/clerk-review-filter/other-filter-operations.model';
import { ALL_QUEUES_VALUE } from '@feature/crl/clerk-review-filter/queue-filter-control/queue-filter-control.component';
import { CLERK_REVIEW_SORT_COLUMN } from '@feature/crl/clerk-review-sort-column';

/**
 * A type alias of ReviewFilingSummary keys.
 * - Provides more type safety when setting the sortBy field in the payload.
 */
export type ReviewFilingSummaryKeys = keyof ReviewFilingSummary;

/**
 * The default payload object used at initialization and when resetting the filter.
 * - Created a constant here to make it easier to reference in tests
 */
export const defaultPayload: ClerkReviewQueryRequest = {
  searchSupportingDocuments: {
    value: true,
    operation: QueryOperation.Equal
  },
  sortBy: CLERK_REVIEW_SORT_COLUMN.DATE_RANGE,
  sortOrder: SortOrder.Descending,
  take: 250,
  skip: 0,
  includeTotal: true
};

/**
 * A service to manage the setting of the ClerkReviewFilterPayload object.
 */
@Injectable({ providedIn: 'root' })
export class ClerkReviewFilterPayloadService {
  /**
   * Thge default IClerkReviewFilterPayload object used at initialization
   * and when resetting the filter.
   */
  private defaultFilterPayload: ClerkReviewQueryRequest = defaultPayload;

  /**
   * A bolean flag to indicate whether the current payload  is a new payload
   * that has not been used yet stored in a BehaviorSubject.
   */
  private isNewPayload$$ = new BehaviorSubject<boolean>(true);
  
  /**
   * A bolean flag to indicate whether the current payload is a new payload 
   * that has not been used yet.
   */
  isNewPayload$ = this.isNewPayload$$.asObservable();

  /**
   * The IClerkReviewFilterPayload object saved in a BehaviorSubject.
   */
  private filterPayload$$ = new BehaviorSubject<ClerkReviewQueryRequest>(this.defaultFilterPayload);

  /**
   * The IClerkReviewFilterPayload object exposed as an Observable.
   */
  filterPayload$ = this.filterPayload$$.asObservable().pipe(
    tap(() => {
      // Payload has been requested so flag it as used.
      this.isNewPayload$$.next(false);
    })
  );

  /**
   * A public method to allow setting of the Court filter.
   * 
   * @param value The courtId 
   */
  setCourtFilter(value: number): void {
    // Create a new IClerkReviewFilterPayload object from the last stored payload.
    const payload: ClerkReviewQueryRequest = {
      ...this.filterPayload$$.value
    };

    // Either set or remove the queueId property in the payload.
    if (value && value !== ALL_COURTS_VALUE) {
      payload.courtId ={
        value: value,
        operation: QueryOperation.Equal
      }
    } else {
      delete payload.courtId;
    }

    // Set the updated IClerkReviewFilterPayload object back into the BehaviorSubject. 
    this.setFilterPayload(payload);
  }

  /**
   * A public method to allow setting of the Queue filter.
   * 
   * @param value The queueId 
   */
  setQueueFilter(value: string) {
    // Create a new IClerkReviewFilterPayload object from the last stored payload.
    const payload: ClerkReviewQueryRequest = {
      ...this.filterPayload$$.value
    };

    // Either set or remove the queueId property in the payload.
    if (value && value !== ALL_QUEUES_VALUE) {
      const queueIds = new Array<string>();
      queueIds.push(value);
      payload.queueIds ={
        value: queueIds,
        operation: QueryOperation.Equal
      }
    } else {
      delete payload.queueIds;
    }

    // Set the updated IClerkReviewFilterPayload object back into the BehaviorSubject. 
    this.setFilterPayload(payload);
  }

  /**
   * A public method to allow setting of the Judge filter.
   * 
   * @param value The judgeId 
   */
  setJudgeFilter(value: number): void {
    // Create a new IClerkReviewFilterPayload object from the last stored payload.
    const payload: ClerkReviewQueryRequest = {
      ...this.filterPayload$$.value
    };

    // Either set or remove the judgeId property in the payload.
    if (value && value !== ALL_JUDGES_VALUE) {
      payload.judgeId = {
        value: value,
        operation: QueryOperation.Equal
      };
    } else {
      delete payload.judgeId;
    }

    // Set the updated IClerkReviewFilterPayload object back into the BehaviorSubject. 
    this.setFilterPayload(payload);
  }

  /**
   * A public method to allow setting of the Date Range filter.
   * 
   * @param dateRange The date range to set the filter for.
   */
  setDateRangeFilter(dateRange: { startDate: Date | null, endDate: Date | null }): void {
    // Create a new IClerkReviewFilterPayload object from the last stored payload.
    const payload: ClerkReviewQueryRequest = {
      ...this.filterPayload$$.value
    };

    // Either set or remove the dateRange property in the payload.
    if (this.isValidDateRange(dateRange.startDate, dateRange.endDate)) {
      payload.dateRange = {
        value: {
          startDate: dateRange.startDate.toISOString(),
          endDate: dateRange.endDate.toISOString()
        },
        operation: QueryOperation.Contains
      };
    } else {
      delete payload.dateRange;
    }

    // Set the updated IClerkReviewFilterPayload object back into the BehaviorSubject. 
    this.setFilterPayload(payload);
  }

  /**
   * A private helper function to check for a valid dateRange
   * 
   * @returns Returns true if valid, false if invalid.
   */
  private isValidDateRange(startDate: Date | null, endDate: Date | null): boolean {
    if (
      !(startDate instanceof Date) || isNaN(startDate.getTime()) ||
      !(endDate instanceof Date) || isNaN(endDate.getTime())
    ) {
      return false; // Invalid dates
    }
  
    return startDate.getTime() <= endDate.getTime();
  }

  /**
   * A public method to allow setting of the Documents filter.
   * 
   * @param value A boolean indicating whether to search supporting documents or not.
   */
  setDocumentsFilter(value: boolean): void {
    // Create a new IClerkReviewFilterPayload object from the last stored payload.
    const payload: ClerkReviewQueryRequest = {
      ...this.filterPayload$$.value
    };

    // Set the searchSupportingDocuments property in the payload.
    payload.searchSupportingDocuments = {
      value: value,
      operation: QueryOperation.Equal
    };

    // Set the updated IClerkReviewFilterPayload object back into the BehaviorSubject. 
    this.setFilterPayload(payload);
  }

  /**
   * A public method to allow setting of the "Other Filter" properties in the payload.
   * 
   * @param field An object containing the property name to which the filter is being applied.
   * 
   * @param operation An object containing the query operation that should be applied.
   * 
   * @param value The string value to filter on.
   */
  setOtherFilter(
    field: IOtherFilterField, 
    operation: IOtherFilterOperation,
    value: string
  ): void {
    // Create a new IClerkReviewFilterPayload object from the last stored payload.
    const payload: ClerkReviewQueryRequest = {...this.filterPayload$$.value };

    // For filingId, convert the string to a number
    if (field.propertyName === 'filingId') {
      // Check if the value is a number.
      const numValue = parseInt(value);
      if (!isNaN(numValue)) {
        // If the value is a number, set the filingId property in the payload.
        payload.filingId = { value: numValue, operation: operation.queryOperation };
      } else {
        // If the value is not a number, remove the filingId property from the payload.
        delete payload.filingId;
      }
      this.setFilterPayload(payload);
      return;
    }

    // For all other fields, set the string value directly
    (payload[field.propertyName] as QueryColumnOfString) = {
      value,
      operation: operation.queryOperation
    };

    // Set the updated IClerkReviewFilterPayload object back into the BehaviorSubject. 
    this.setFilterPayload(payload);
  }

  /**
   * A public method to allow for the removal of "Other Filter" properties 
   * from the payload.
   * 
   * @param field An object containing the property name of the filter to be removed.
   */
  removeOtherFilter(
    field: IOtherFilterField
  ): void {
    // Create a new IClerkReviewFilterPayload object from the last stored payload.
    const payload: ClerkReviewQueryRequest = {
      ...this.filterPayload$$.value
    };

    // Remove the property from the payload.
    delete payload[field.propertyName];

    // Set the updated IClerkReviewFilterPayload object back into the BehaviorSubject. 
    this.setFilterPayload(payload);
  }

  /**
   * A public method to set the take (page size) property. 
   * 
   * @param value The number of items to return.
   */
  setTake(value: number): void {
    // Create a new IClerkReviewFilterPayload object from the last stored payload.
    const payload: ClerkReviewQueryRequest = {
      ...this.filterPayload$$.value
    };

    // Set the take property in the payload.
    payload.take = value;

    // Set the updated IClerkReviewFilterPayload object back into the BehaviorSubject. 
    this.setFilterPayload(payload);
  }

  /**
   * A public method to set the skip property. 
   * 
   * @param value The number of items to skip.
   */
  setSkip(value: number): void {
    // Create a new IClerkReviewFilterPayload object from the last stored payload.
    const payload: ClerkReviewQueryRequest = {
      ...this.filterPayload$$.value
    };

    // Set the skip property in the payload.
    payload.skip = value;

    // Set the updated IClerkReviewFilterPayload object back into the BehaviorSubject. 
    this.setFilterPayload(payload);
  }

  /**
   * A public method to allow setting of the sort column and direction.
   * - The grid's sort headers choose the direction: a column's first click
   *   uses its start direction and later clicks toggle. Taking it as given
   *   keeps the payload matching the header even when the stored sort differs
   *   from the applied one (for example after Reset without Apply).
   *
   * @param sortBy The sortBy property to set.
   * @param sortOrder The sortOrder property to set.
   */
  setSortBy(
    sortBy: CLERK_REVIEW_SORT_COLUMN,
    sortOrder: SortOrder = SortOrder.Ascending,
  ): void {
    // Create a new IClerkReviewFilterPayload object from the last stored payload.
    const payload: ClerkReviewQueryRequest = {
      ...this.filterPayload$$.value,
      sortBy,
      sortOrder,
    };

    // Set the updated IClerkReviewFilterPayload object back into the BehaviorSubject.
    this.setFilterPayload(payload);
  }

  /**
   * A public method to allow resetting of the filter.
   */
  resetPayload(): void {
    this.setFilterPayload(this.defaultFilterPayload);
  }

  /**
   * A public method to allow the payload object to be set either internally or 
   * externally from stored values service. 
   *   
   * @param payload The payload object to set. 
   */
  setFilterPayload(payload: ClerkReviewQueryRequest): void {
    this.filterPayload$$.next(payload);
    this.isNewPayload$$.next(true); // Ensure app treats this as a new payload.
  }
}
